import StockOpnameEntriesApiV2 from 'api/v2/admins/stock-opname-entries-api-v2'
import PaginationBar from 'components/organisms/pagination-bar/pagination-bar'
import TitlePage from 'components/atoms/title-page'
import { ALERT_TYPES } from 'constants/alert-constants'
import PropTypes from 'prop-types'
import React, { useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router'
import { Badge, Button, ButtonGroup, Card, CardBody, Col, Input, InputGroup, InputGroupAddon, Row, Spinner, Table } from 'reactstrap'

const propTypes = {
  pageUtils: PropTypes.shape({
    setAlertMsg    : PropTypes.func,
    setApiErrorMsg : PropTypes.func
  })
}

const SCOPE_OPTIONS = [
  { key: 'in_progress', label: 'In Progress' },
  { key: 'cek_fisik', label: 'Need Cek Fisik' },
  { key: 'cek_kelengkapan', label: 'Need Cek Kelengkapan' },
  { key: 'all', label: 'All' }
]

const resolveInitialScope = () => {
  const params = new URLSearchParams(window.location.search)
  const scope = params.get('scope')

  return SCOPE_OPTIONS.some((option) => option.key === scope) ? scope : 'in_progress'
}

const resolveInitialPage = () => {
  const params = new URLSearchParams(window.location.search)
  const page = Number(params.get('page') || 1)

  return Number.isFinite(page) && page > 0 ? Math.floor(page) : 1
}

const stockOpnameEntryListPath = ({ query, scope, page }) => {
  const params = new URLSearchParams()

  params.set('scope', scope || 'in_progress')
  if (query.trim()) params.set('q', query.trim())
  if (page > 1) params.set('page', String(page))

  return `/app/stock-opname-entries/list-page?${params.toString()}`
}

const physicalCheckBadge = (entry) => {
  if (entry?.physical_check_status === 'found') return <Badge color='success'>{ '✓ Ada' }</Badge>
  if (entry?.physical_check_status === 'not_found') return <Badge color='danger'>{ '✕ Tidak Ada' }</Badge>

  return <Badge color='secondary'>{ 'Pending' }</Badge>
}

const booleanBadge = (value) => {
  return value
    ? <Badge color='success'>{ '✓' }</Badge>
    : <Badge color='secondary'>{ '-' }</Badge>
}

const StockOpnameEntryListPage = ({ pageUtils }) => {
  const history = useHistory()
  const [ entries, setEntries ] = useState([])
  const [ query, setQuery ] = useState(() => new URLSearchParams(window.location.search).get('q') || '')
  const [ scope, setScope ] = useState(resolveInitialScope)
  const [ pagination, setPagination ] = useState({ current_page: 1, total_pages: 1, total_count: 0, per_page: 25 })
  const [ scopeCounts, setScopeCounts ] = useState({ all: 0, in_progress: 0, cek_fisik: 0, cek_kelengkapan: 0 })
  const [ page, setPage ] = useState(resolveInitialPage)
  const [ isLoading, setIsLoading ] = useState(true)
  const latestRequestRef = useRef(0)

  const fetchEntries = () => {
    const requestId = latestRequestRef.current + 1
    latestRequestRef.current = requestId
    setIsLoading(true)

    StockOpnameEntriesApiV2.get({ q: query, scope, page, per_page: 25 })
      .then((response) => {
        if (requestId !== latestRequestRef.current) return
        setEntries(response?.data?.stock_opname_entries || [])
        setPagination(response?.data?.meta || { current_page: page, total_pages: 1, total_count: 0, per_page: 25 })
        setScopeCounts(response?.data?.meta?.scope_counts || { all: 0, in_progress: 0, cek_fisik: 0, cek_kelengkapan: 0 })
      })
      .catch((error) => {
        if (requestId !== latestRequestRef.current) return
        pageUtils?.setApiErrorMsg?.(error?.response?.data)
        pageUtils?.setAlertMsg?.('Unable to fetch stock opname entries', ALERT_TYPES.DANGER)
      })
      .finally(() => {
        if (requestId !== latestRequestRef.current) return
        setIsLoading(false)
      })
  }

  useEffect(() => {
    const nextUrl = stockOpnameEntryListPath({ query, scope, page })
    window.history.replaceState({}, '', nextUrl)
  }, [ query, scope, page ])

  useEffect(() => {
    const timeoutId = setTimeout(fetchEntries, 150)

    return () => clearTimeout(timeoutId)
  }, [ query, scope, page ]) // eslint-disable-line react-hooks/exhaustive-deps

  const openEntry = (entry) => {
    const returnTo = stockOpnameEntryListPath({ query, scope, page })
    history.push(`/app/stock-opname-entries/${entry.id}/edit-page?return_to=${encodeURIComponent(returnTo)}`)
  }

  return (
    <>
      <TitlePage mainTitle={ 'Stock Opname Entries' } subTitle={ 'List' } />

      <Row className='mb-3'>
        <Col>
          <ButtonGroup className='d-flex flex-wrap'>
            {
              SCOPE_OPTIONS.map((option) => (
                <Button
                  key={ option.key }
                  color={ scope === option.key ? 'primary' : 'secondary' }
                  outline={ scope !== option.key }
                  onClick={ () => { setScope(option.key); setPage(1) } }
                >
                  { `${option.label} (${scopeCounts?.[option.key] || 0})` }
                </Button>
              ))
            }
          </ButtonGroup>
        </Col>
      </Row>

      <Card>
        <CardBody>
          <Row className='mb-3'>
            <Col md={ 5 }>
              <InputGroup>
                <Input
                  value={ query }
                  onChange={ (event) => { setQuery(event.target.value); setPage(1) } }
                  placeholder='Search product, SKU, supplier, stock opname'
                />
                { query ? (
                  <InputGroupAddon addonType='append'>
                    <Button
                      color='secondary'
                      outline
                      onClick={ () => { setQuery(''); setPage(1) } }
                      aria-label='Clear search'
                      title='Clear search'
                    >
                      { '×' }
                    </Button>
                  </InputGroupAddon>
                ) : null }
              </InputGroup>
            </Col>
          </Row>

          {
            isLoading && entries.length === 0
              ? <Spinner color='primary' />
              : (
                <>
                  <div className='d-none d-md-block'>
                    <Table hover responsive>
                      <thead>
                        <tr>
                          <th>{ 'Stock Opname' }</th>
                          <th style={{ maxWidth: '280px' }}>{ 'Product Name' }</th>
                          <th>{ 'Stock Code' }</th>
                          <th>{ 'Inventory' }</th>
                          <th>{ 'Sellable Quantity' }</th>
                          <th>{ 'Inventory Quantity' }</th>
                          <th>{ 'Inventory Location' }</th>
                          <th>{ 'Supplier' }</th>
                          <th>{ 'Cek Fisik' }</th>
                          <th>{ 'Cek Kelengkapan' }</th>
                          <th>{ 'Price Tag' }</th>
                        </tr>
                      </thead>
                      <tbody>
                        {
                          entries.map((entry) => (
                            <tr
                              key={ entry.id }
                              onClick={ () => openEntry(entry) }
                              style={{ cursor: 'pointer' }}
                            >
                              <td>{ entry.stock_opname_title || '-' }</td>
                              <td style={{ maxWidth: '280px', whiteSpace: 'normal', wordBreak: 'break-word' }}>{ entry.product_name || '-' }</td>
                              <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{ entry.stock_code || '-' }</td>
                              <td>{ entry.inventory_id || '-' }</td>
                              <td>{ entry.current_sellable_quantity ?? '-' }</td>
                              <td>{ entry.current_inventory_quantity ?? '-' }</td>
                              <td style={{ maxWidth: '260px', whiteSpace: 'normal', wordBreak: 'break-word' }}>{ entry.current_inventory_location_display || '-' }</td>
                              <td style={{ maxWidth: '220px', whiteSpace: 'normal', wordBreak: 'break-word' }}>{ entry.supplier || '-' }</td>
                              <td>{ physicalCheckBadge(entry) }</td>
                              <td>{ booleanBadge(entry.all_inclusions_found) }</td>
                              <td>{ booleanBadge(entry.price_tag_exist) }</td>
                            </tr>
                          ))
                        }
                      </tbody>
                    </Table>
                  </div>

                  <div className='d-md-none'>
                    {
                      entries.map((entry) => (
                        <Card
                          key={ entry.id }
                          className='mb-2'
                          onClick={ () => openEntry(entry) }
                          style={{ cursor: 'pointer' }}
                        >
                          <CardBody className='py-3'>
                            <div><strong>{ entry.stock_code || '-' }</strong></div>
                            <div className='mb-2' style={{ wordBreak: 'break-word' }}>{ entry.product_name || '-' }</div>
                            <div>{ `STO: ${entry.stock_opname_title || '-'}` }</div>
                            <div>{ `Inventory: ${entry.inventory_id || '-'} · SQ: ${entry.current_sellable_quantity ?? '-'} · IQ: ${entry.current_inventory_quantity ?? '-'}` }</div>
                            <div style={{ wordBreak: 'break-word' }}>{ `Location: ${entry.current_inventory_location_display || '-'}` }</div>
                            <div style={{ wordBreak: 'break-word' }}>{ `Supplier: ${entry.supplier || '-'}` }</div>
                            <div className='mt-2'>
                              <span className='mr-2'>{ physicalCheckBadge(entry) }</span>
                              <span className='mr-2'>{ 'Kelengkapan ' }{ booleanBadge(entry.all_inclusions_found) }</span>
                              <span>{ 'Price Tag ' }{ booleanBadge(entry.price_tag_exist) }</span>
                            </div>
                          </CardBody>
                        </Card>
                      ))
                    }
                  </div>
                </>
              )
          }

          {
            isLoading && entries.length > 0 ? (
              <div className='mb-2'><small className='text-muted'>{ 'Updating results...' }</small></div>
            ) : null
          }

          {
            !isLoading || entries.length > 0 ? (
              <div className='d-flex justify-content-between align-items-center flex-wrap mt-3'>
                <small className='text-muted mb-2'>
                  { pagination?.total_count > 0
                    ? `Showing ${((pagination.current_page - 1) * pagination.per_page) + 1}-${Math.min(pagination.current_page * pagination.per_page, pagination.total_count)} of ${pagination.total_count}`
                    : 'Showing 0 of 0' }
                </small>
                { pagination?.total_pages > 1 ? (
                  <PaginationBar
                    pagination={ pagination }
                    onClickPage={ (nextPage) => setPage(nextPage) }
                  />
                ) : null }
              </div>
            ) : null
          }
        </CardBody>
      </Card>
    </>
  )
}

StockOpnameEntryListPage.propTypes = propTypes

export default StockOpnameEntryListPage
